import { useContext, useMemo, useState } from 'react';
import { toJpeg, toPng } from 'html-to-image';
import { downloadImage } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/export.util.ts';
import { Options } from 'html-to-image/src/types.ts';
import { useReactFlow } from '@xyflow/react';
import { useCapChartPageHeaderController as useCapChartSettingsController } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/useCapChartPageHeaderController.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getCapChartData } from '@dealstack/frontend/app/services/CapTableData.service.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { CapTableRail, GetGroupStructureDataRes } from '@dealstack/shared/lib/generated';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import type { CapChartLocalStorageSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSettings.types.ts';

// Typed so the query data stays GetGroupStructureDataRes (nomineeFlags is optional) even on the
// no-deal fallback, keeping capChartData.nomineeFlags accessible without a union narrowing.
const EMPTY_CHART: GetGroupStructureDataRes = { nodes: [], edges: [] };

const downloadOptions: Options = {
	backgroundColor: '#ffffff',
	skipFonts: true,
	preferredFontFormat: 'woff2',
};

/** html-to-image clamps at 16384, so this halves the long edge and quarters the pixels to encode. */
const EXPORT_MAX_LONG_EDGE_PX = 8192;

interface Props {
	appliedSecurityIds: string[] | undefined;
	isoFilterDate: string | undefined;
}

export const useCapChartDataController = ({ appliedSecurityIds, isoFilterDate }: Props) => {
	const { dealId, deal } = useContext(DealContext);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [isMasterView, setIsMasterView] = useState(false);
	const rfInstance = useReactFlow();

	const isFundDeal = deal?.isFund ?? false;
	const dealFundId = deal?.fundId;

	const {
		valueDisplayMode,
		valuesHeldTypes,
		numberFormat,
		onValuesHeldTypesChange,
		onValueDisplayModeChange,
		onNumberFormatChange,
		onResetSettings,
		rail,
		setRail,
	} = useCapChartSettingsController(dealId);

	// CAP-1062: the feature flag keeps every nominee surface dark. With the flag off the rail
	// toggle is hidden and the query always requests the beneficial (single-rail) projection,
	// even if a previously stored setting says otherwise.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	const effectiveRail = nomineeStructuresEnabled ? rail : CapTableRail.BENEFICIAL;

	const fundId = isFundDeal && isMasterView ? dealFundId : undefined;

	const { data: capChartData, isLoading: isLoadingChartData } = useQuery({
		queryKey: [
			'chartData',
			dealId,
			isoFilterDate,
			appliedSecurityIds,
			isMasterView,
			fundId,
			effectiveRail,
		],
		queryFn: () => {
			if (!dealId) {
				return Promise.resolve(EMPTY_CHART);
			}
			return getCapChartData({
				filterDate: isoFilterDate,
				securityIds: appliedSecurityIds,
				isMasterView,
				fundId,
				rail: effectiveRail,
			});
		},
		enabled: !!dealId,
		placeholderData: keepPreviousData,
	});

	const chartSettings: CapChartLocalStorageSettings = useMemo(
		() => ({
			valueDisplayMode,
			valuesHeldTypes,
			numberFormat,
		}),
		[valueDisplayMode, valuesHeldTypes, numberFormat]
	);

	const downloadAs = async (format: 'png' | 'jpg', fileName?: string) => {
		if (!rfInstance) {
			return;
		}
		setDownloadLoading(true);
		const reactFlowClone = rfInstance.toObject();
		await rfInstance.fitView({
			padding: 0.1,
			includeHiddenNodes: false,
			minZoom: 0.001,
			maxZoom: 1,
			duration: 0,
		});
		const zoom = rfInstance.getZoom();
		let pixelRatio = 1 / zoom;
		if (pixelRatio < 1) {
			pixelRatio = 1;
		}
		const viewportEl: HTMLElement | null = document.querySelector('.react-flow__viewport');
		if (!viewportEl) {
			setDownloadLoading(false);
			return;
		}

		try {
			// The capture element spans the whole pane, so an unbounded ratio asks for a canvas so
			// large that rasterising and encoding it blocks the main thread and looks like a hang.
			const longEdge = Math.max(viewportEl.clientWidth, viewportEl.clientHeight) || 1;
			const options = {
				...downloadOptions,
				pixelRatio: Math.max(
					1,
					Math.min(pixelRatio * 10, EXPORT_MAX_LONG_EDGE_PX / longEdge)
				),
			};
			if (format === 'jpg') {
				await toJpeg(viewportEl, options).then((url) =>
					downloadImage(url, fileName, 'jpg')
				);
			} else {
				await toPng(viewportEl, options).then((url) => downloadImage(url, fileName, 'png'));
			}
		} finally {
			// A failed export otherwise leaves the chart fitted for capture and every export option
			// disabled, with a reload the only way back.
			rfInstance.setNodes(reactFlowClone.nodes);
			rfInstance.setEdges(reactFlowClone.edges);
			void rfInstance.zoomTo(reactFlowClone.viewport.zoom, { duration: 0 });
			void rfInstance.setViewport(reactFlowClone.viewport, { duration: 0 });
			setTimeout(() => setDownloadLoading(false), 100);
		}
	};

	return {
		dealId,
		dealName: deal?.name,
		downloadAs,
		downloadLoading,
		rfInstance,
		onValuesHeldTypesChange,
		onValueDisplayModeChange,
		onNumberFormatChange,
		onResetSettings,
		chartSettings,
		capChartData,
		isLoadingChartData,
		isFundDeal,
		isMasterView,
		setIsMasterView,
		rail: effectiveRail,
		setRail,
		hasNomineeStructure:
			nomineeStructuresEnabled && (capChartData?.nomineeFlags?.hasNomineeStructure ?? false),
	};
};
